import React, { useEffect, useState, useCallback } from "react";
import { API, getSessionId, setSessionId } from "./api";
import LiveStudio from "./components/LiveStudio";
import MemoryPalace from "./components/MemoryPalace";
import Inspector from "./components/Inspector";
import {
  Zap,
  Boxes,
  Braces,
  RotateCcw,
  ShieldAlert,
  Cpu,
  CircleDot,
} from "lucide-react";

const TABS = [
  { id: "studio", label: "Live Studio", icon: Zap },
  { id: "palace", label: "Memory Palace", icon: Boxes },
  { id: "inspector", label: "Metadata Inspector", icon: Braces },
];

export default function App() {
  const [sid, setSid] = useState(null);
  const [tab, setTab] = useState("studio");
  const [config, setConfig] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const [memories, setMemories] = useState([]);
  const [bannerOpen, setBannerOpen] = useState(true);
  const [bootError, setBootError] = useState("");
  const [resetVersion, setResetVersion] = useState(0);
  const [memoryRevision, setMemoryRevision] = useState(0);
  const [resetError, setResetError] = useState("");

  const loadMemories = useCallback(async (s) => {
    if (!s) return;
    setMemories(await API.memories(s));
  }, []);

  useEffect(() => {
    (async () => {
      try {
      let s = getSessionId();
      if (!s) {
        const created = await API.createSession();
        s = created.id;
        setSessionId(s);
      }
      setSid(s);
      const [cfg, strat, rules] = await Promise.all([API.config(), API.strategies(), API.heuristics()]);
      setConfig({ ...cfg, heuristics: rules });
      setStrategies(strat.strategies);
      await loadMemories(s);
      } catch (_) { setBootError("Could not open your sandbox. Please retry."); }
    })();
  }, [loadMemories]);

  const handleReset = async () => {
    if (!sid) return;
    if (!window.confirm("Reset this sandbox to the seeded demo palace?")) return;
    try {
      setResetError("");
      await API.reset(sid);
      await loadMemories(sid);
      setLastEvent(null);
      setResetVersion((v) => v + 1);
    } catch (_) { setResetError("Could not reset the sandbox. Please retry."); }
  };

  if (bootError) return <div className="p-8 text-rose" data-testid="boot-error">{bootError}
    <button data-testid="boot-retry" className="ml-3 underline" onClick={() => window.location.reload()}>Retry</button></div>;
  if (!sid || !config) {
    return (
      <div data-testid="boot-loading" className="app-bg min-h-screen flex items-center justify-center text-muted font-mono">
        booting WisprLink…
      </div>
    );
  }

  return (
    <div className="app-bg min-h-screen text-fg font-body">
      <header className="border-b border-line bg-panel/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber flex items-center justify-center text-ink font-display font-extrabold">
              W
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">
                Wispr<span className="text-amber">Link</span>
              </div>
              <div className="text-[11px] text-muted font-mono">memory-graph enrichment workbench</div>
            </div>
          </div>

          <nav className="flex flex-wrap items-center gap-1" data-testid="tab-nav">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tab === t.id ? "tab-active" : "text-muted hover:text-fg hover:bg-panel2"
                  }`}
                >
                  <Icon size={15} />
                  {t.label}
                </button>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-3 text-xs font-mono">
            <span
              className="flex items-center gap-1.5 text-muted"
              title="Semantic embedding backend in use"
              data-testid="embedding-badge"
            >
              <Cpu size={13} className="text-teal" />
              {config.embedding_backend}
            </span>
            <span
              className={`flex items-center gap-1.5 ${config.openrouter_configured ? "text-lime" : "text-muted"}`}
              title={config.openrouter_configured ? `OpenRouter: ${config.openrouter_model}` : "OpenRouter key not set — LLM features fall back to local logic"}
              data-testid="llm-badge"
            >
              <CircleDot size={11} />
              {config.openrouter_configured ? "LLM on" : "LLM off"}
            </span>
            <span className="text-muted hidden md:inline">{sid}</span>
            <button
              onClick={handleReset}
              data-testid="reset-sandbox-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line text-muted hover:text-rose hover:border-rose/50 transition-colors"
            >
              <RotateCcw size={13} /> Reset sandbox
            </button>
          </div>
        </div>
      </header>

      {bannerOpen && (
        <div className="bg-rose/10 border-b border-rose/20 text-rose text-[13px]" data-testid="privacy-banner">
          <div className="max-w-[1500px] mx-auto px-5 py-2 flex items-center gap-2">
            <ShieldAlert size={15} />
            Public demo — don't paste secrets or personal data. Sandbox records expire after 7 days.
            <button data-testid="privacy-dismiss" className="ml-auto text-rose/70 hover:text-rose" onClick={() => setBannerOpen(false)}>
              dismiss
            </button>
          </div>
        </div>
      )}

      <main className="max-w-[1500px] mx-auto px-5 py-5">
        {resetError && <div data-testid="reset-error" role="alert" className="text-rose text-xs mb-3">{resetError}</div>}
        <div hidden={tab !== "studio"}>
          <LiveStudio
            key={resetVersion}
            sid={sid}
            strategies={strategies}
            config={config}
            memories={memories}
            memoryRevision={memoryRevision}
            reloadMemories={() => loadMemories(sid)}
            onEvent={setLastEvent}
          />
        </div>
        {tab === "palace" && (
          <MemoryPalace sid={sid} memories={memories} reloadMemories={async () => { await loadMemories(sid); setMemoryRevision((v) => v + 1); }} />
        )}
        {tab === "inspector" && <Inspector event={lastEvent} sid={sid} />}
      </main>
      <footer data-testid="scope-notice" className="max-w-[1500px] mx-auto px-5 pb-6 text-xs text-muted">Workbench demo · Context from other apps requires manual bindings or a Context Pack. Local text matching; no external app access.</footer>
    </div>
  );
}
